import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

// Formik
import { Form, Formik } from "formik";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import ButtonBase from "@material-ui/core/ButtonBase";

// Config
import { useNewCaseFlow } from "src/context/NewCaseFlowProvider";
import { useOrganizations } from "src/context/OrganizationsProvider";
import colors from "../../config/colors";
import useTranslate from "../../hooks/useTranslate";

// Components
import Button from "../Button";
import Text from "../Text";
import CustomField from "../CustomField";
import Close from "../Close";

const useStyles = makeStyles(theme => ({
  dialog: {
    maxWidth: ({ large }) => large && "700px"
  },
  root: {
    padding: theme.spacing(4, 3, 5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: ({ large }) => (large ? "100%" : 465)
  },
  buttonClose: {
    display: "flex",
    marginLeft: "auto",
    padding: theme.spacing(2)
  },
  heading: {
    marginBottom: theme.spacing(2),
    textAlign: "left",
    width: "100%"
  },
  label: {
    marginBottom: theme.spacing(1),
    fontSize: 15
  },
  form: {
    width: "100%"
  },
  buttons: {
    display: "flex",
    flexDirection: "column",
    width: "100%"
  },
  button: {
    minWidth: 144,
    height: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    marginBottom: theme.spacing(1.5)
  }
}));

const EMPTY_OPTION = "__empty__";
const OTHER_OPTION = "__other__";

const ReferenceNumberDialog = ({ onClose, initialValue }) => {
  const classes = useStyles();
  const getText = useTranslate();
  const { open: openNewCaseFlow } = useNewCaseFlow();
  const [open, setOpen] = useState(true);
  const { organizations, loading } = useOrganizations();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleNext = values => {
    const params = {
      ref: values.referenceNumber
    };

    if (values.organization === OTHER_OPTION) {
      params.orgName = values.organizationName;
    } else {
      params.orgId = values.organization;
    }

    openNewCaseFlow(params);
    setOpen(false);
  };

  const options = useMemo(() => {
    const result = [
      {
        value: EMPTY_OPTION,
        text: getText("common.prompts.pleaseSelect"),
        props: { disabled: true }
      },
      ...organizations.map(({ id, name }) => ({
        value: id,
        text: name
      })),
      {
        value: OTHER_OPTION,
        text: getText("common.prompts.other")
      }
    ];

    return result;
  }, [organizations]);

  const canSubmit = values => {
    if (loading) return false;
    if (!values.referenceNumber) return false;

    if (values.organization === EMPTY_OPTION) {
      return false;
    }

    if (
      values.organization === OTHER_OPTION &&
      !values.organizationName?.trim()
    ) {
      return false;
    }
    return true;
  };

  return (
    <Dialog
      classes={{
        paper: classes.dialog
      }}
      onClose={handleCancel}
      open={open}
      TransitionProps={{
        onExited: onClose
      }}
    >
      <div className={classes.root}>
        <ButtonBase className={classes.buttonClose} onClick={handleCancel}>
          <Close size={16} color={colors.riverBed} />
        </ButtonBase>
        <Text
          type="heading3"
          className={classes.heading}
          text="createCasePage.givenReferenceNumber.title"
        />
        <Formik
          initialValues={{
            referenceNumber: initialValue,
            organization: EMPTY_OPTION,
            organizationName: ""
          }}
          onSubmit={handleNext}
        >
          {({ values }) => (
            <Form className={classes.form}>
              <Text
                type="heading6"
                className={classes.label}
                text="createCasePage.givenReferenceNumber.referenceNumberLabel"
              />
              <CustomField
                autoComplete="off"
                name="referenceNumber"
                placeholder={getText(
                  "createCasePage.givenReferenceNumber.referenceNumberLabel"
                )}
              />
              <Text
                type="heading6"
                className={classes.label}
                text="createCasePage.givenReferenceNumber.organisationQuestionLabel"
              />
              <CustomField
                autoComplete="off"
                disabled={loading}
                name="organization"
                options={options}
                type="select"
                literalOptionText
              />
              {values.organization === OTHER_OPTION && (
                <>
                  <Text
                    type="heading6"
                    className={classes.label}
                    text="createCasePage.givenReferenceNumber.organisationNameLabel"
                  />
                  <CustomField
                    autoComplete="off"
                    name="organizationName"
                    placeholder={getText(
                      "createCasePage.givenReferenceNumber.organisationNameLabel"
                    )}
                  />
                </>
              )}
              <div className={classes.buttons}>
                <Button
                  className={classes.button}
                  disableElevation
                  disabled={!canSubmit(values)}
                  variant="contained"
                  type="submit"
                  color="primary"
                >
                  {getText("common.prompts.next")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Dialog>
  );
};

ReferenceNumberDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialValue: PropTypes.string
};

ReferenceNumberDialog.defaultProps = {
  initialValue: ""
};

export default ReferenceNumberDialog;
